import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface SchoolNotesPanelProps {
  schoolId: string;
  notes: string;
  onSave: (notes: string) => Promise<void> | void;
}

export function SchoolNotesPanel({ schoolId, notes, onSave }: SchoolNotesPanelProps) {
  const [draftNotes, setDraftNotes] = useState(notes);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraftNotes(notes);
    setSaved(false);
  }, [schoolId, notes]);

  async function saveNotes() {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(draftNotes.trim());
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-white p-4">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-subtle">
        Your notes
      </p>
      <Textarea
        value={draftNotes}
        onChange={event => {
          setDraftNotes(event.target.value);
          setSaved(false);
        }}
        placeholder="Add visit impressions, coach details, questions, or anything you want to remember…"
        className="mt-3 min-h-[92px] resize-y rounded-xl border-border bg-surface text-[11px] leading-relaxed"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium text-subtle">
          {saved ? "Notes saved" : "Only you can see these notes."}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={saveNotes}
          disabled={saving || draftNotes.trim() === notes}
          className="h-8 rounded-lg text-[11px] font-extrabold"
        >
          {saving ? "Saving…" : "Save notes"}
        </Button>
      </div>
    </section>
  );
}
